import React, { useState, useEffect, useCallback } from 'react';
import { FaUser, FaPlus, FaEdit, FaTrash, FaMicrophone, FaClock, FaFilter } from 'react-icons/fa';
import firebaseService from '../../services/firebaseService';
import './RecentActivity.css';

const RecentActivity = ({ limit = 10 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, proverbs, voices
  const [showAll, setShowAll] = useState(false);

  // Helper function to get user name (defined early for use in fetchActivities)
  const getUserName = (activity) => {
    // Check multiple possible fields for user name
    return activity.userName || 
           activity.user?.name || 
           activity.user?.displayName ||
           activity.userDisplayName ||
           activity.details?.userName ||
           'System User';
  };

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let fetchedActivities = [];
      
      if (filter === 'all') {
        fetchedActivities = await firebaseService.activities.getAllActivities();
      } else if (filter === 'proverbs') {
        fetchedActivities = await firebaseService.activities.getAllActivities();
        fetchedActivities = fetchedActivities.filter(activity => 
          ['PROVERB_CREATED', 'PROVERB_UPDATED', 'PROVERB_DELETED'].includes(activity.action)
        ).sort((a, b) => 
          new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)
        );
      } else if (filter === 'voices') {
        fetchedActivities = await firebaseService.activities.getAllActivities();
        fetchedActivities = fetchedActivities.filter(activity => 
          ['voice_created', 'voice_updated', 'voice_deleted'].includes(activity.action)
        ).sort((a, b) => 
          new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)
        );
      }
      
      console.log(`📊 Fetched ${fetchedActivities.length} activities for filter: ${filter}`, fetchedActivities);
      
      // Filter out System User activities
      const filteredActivities = fetchedActivities.filter(activity => {
        const userName = getUserName(activity);
        return userName !== 'System User' && userName !== 'Unknown User';
      });
      
      console.log(`🔍 After filtering: ${filteredActivities.length} activities (removed System/Unknown users)`);
      setActivities(filteredActivities || []);
    } catch (error) {
      console.error('❌ Failed to fetch activities:', error);
      setError('Failed to load recent activities');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const getActivityIcon = (action) => {
    switch (action) {
      case 'PROVERB_CREATED':
        return <FaPlus className="activity-icon create" />;
      case 'PROVERB_UPDATED':
        return <FaEdit className="activity-icon update" />;
      case 'PROVERB_DELETED':
        return <FaTrash className="activity-icon delete" />;
      case 'voice_created':
      case 'voice_updated':
        return <FaMicrophone className="activity-icon voice" />;
      case 'voice_deleted':
        return <FaTrash className="activity-icon delete" />;
      default:
        return <FaUser className="activity-icon default" />;
    }
  };

  const getActivityColor = (action) => {
    switch (action) {
      case 'PROVERB_CREATED':
      case 'voice_created':
        return 'success';
      case 'PROVERB_UPDATED':
      case 'voice_updated':
        return 'info';
      case 'PROVERB_DELETED':
      case 'voice_deleted':
        return 'default'; // Changed from 'danger' to 'default'
      default:
        return 'default';
    }
  };

  const getUserInitials = (userName) => {
    if (!userName || userName === 'Unknown User') return 'U';
    
    const names = userName.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return userName.slice(0, 2).toUpperCase();
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    let date;
    if (timestamp.seconds) {
      // Firestore Timestamp
      date = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      date = timestamp;
    }
    
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getActivityMessage = (activity) => {
    const details = activity.details || {};
    
    switch (activity.action) {
      case 'PROVERB_CREATED':
        return `Created proverb${details.proverbId ? ` #${details.proverbId}` : ''}`;
      case 'PROVERB_UPDATED':
        return `Updated proverb${details.proverbId ? ` #${details.proverbId}` : ''}`;
      case 'PROVERB_DELETED':
        return `Deleted proverb${details.proverbId ? ` #${details.proverbId}` : ''}`;
      case 'voice_created':
        return `Added voice recording${details.proverbId ? ` for proverb #${details.proverbId}` : ''}`;
      case 'voice_updated':
        return `Updated voice recording${details.proverbId ? ` for proverb #${details.proverbId}` : ''}`;
      case 'voice_deleted':
        return `Deleted voice recording${details.voiceId ? ` (ID: ${details.voiceId})` : ''}`;
      default:
        return activity.description || 'Unknown activity';
    }
  };

  const displayedActivities = showAll ? activities : activities.slice(0, limit);

  if (loading) {
    return (
      <div className="recent-activity-container">
        <div className="recent-activity-header">
          <h3>
            <FaClock className="header-icon" />
            Recent Activity
          </h3>
        </div>
        <div className="recent-activity-loading">
          <div className="loading-spinner"></div>
          <span>Loading activities...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-activity-container">
        <div className="recent-activity-header">
          <h3>
            <FaClock className="header-icon" />
            Recent Activity
          </h3>
        </div>
        <div className="recent-activity-error">
          <span>❌ {error}</span>
          <button onClick={fetchActivities} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-activity-container">
      <div className="recent-activity-header">
        <h3>
          <FaClock className="header-icon" />
          Recent Activity
        </h3>
        <div className="activity-filters">
          <FaFilter className="filter-icon" />
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Activities</option>
            <option value="proverbs">Proverbs Only</option>
            <option value="voices">Voices Only</option>
          </select>
        </div>
      </div>

      <div className="recent-activity-content">
        {displayedActivities.length === 0 ? (
          <div className="no-activities">
            <FaClock className="no-activities-icon" />
            <p>No recent user activities found</p>
            <small>User activities will appear here when users interact with proverbs and voices</small>
          </div>
        ) : (
          <>
            <div className="activity-list">
              {displayedActivities.map((activity, index) => (
                <div 
                  key={activity.id || index} 
                  className={`activity-item ${getActivityColor(activity.action)}`}
                >
                  <div className="activity-icon-container">
                    {getActivityIcon(activity.action)}
                  </div>
                  
                  <div className="activity-details">
                    <div className="activity-main">
                      <span className="activity-user">
                        <span className="user-avatar">
                          {getUserInitials(getUserName(activity))}
                        </span>
                        {getUserName(activity)}
                      </span>
                      <span className="activity-message">{getActivityMessage(activity)}</span>
                    </div>
                    
                    <div className="activity-meta">
                      <span className="activity-time">
                        {formatTimestamp(activity.timestamp || activity.createdAt)}
                      </span>
                      {activity.details?.fileName && (
                        <span className="activity-file">
                          📎 {activity.details.fileName}
                        </span>
                      )}
                    </div>
                    
                    {activity.details?.description && (
                      <div className="activity-description">
                        {activity.details.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {activities.length > limit && (
              <div className="activity-actions">
                <button 
                  onClick={() => setShowAll(!showAll)}
                  className="show-more-btn"
                >
                  {showAll ? 'Show Less' : `Show All (${activities.length})`}
                </button>
                <button onClick={fetchActivities} className="refresh-btn">
                  Refresh
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RecentActivity;
